import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { BlockRenderer } from "@/components/builder/BlockRenderer";

/**
 * Public renderer for platform pages stored in `platform_pages` collection.
 * URL: /<slug>  (e.g. /impressum, /datenschutz, /agb).
 * Falls through to 404 redirect when the slug is unknown.
 */
export default function PublicPlatformPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined); // undefined = loading, null = not found
  const [content, setContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/public/platform-page/${slug}`).then(({ data }) => {
      if (!cancelled) setPage(data);
    }).catch(() => { if (!cancelled) setPage(null); });
    api.get("/public/platform-content").then(({ data }) => { if (!cancelled) setContent(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  // SEO (runs whenever the page changes; safe with null)
  useEffect(() => {
    if (!page) return;
    document.title = page.seo?.title || `${page.title} – StudioMV`;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    if (page.seo?.description) meta.setAttribute("content", page.seo.description);
  }, [page]);

  if (page === undefined) return <div className="min-h-screen flex items-center justify-center text-sm" style={{ color: "var(--mv-text-muted)" }}>Lade…</div>;
  if (page === null) return <Navigate to="/" replace />;

  const footer = content?.footer || { links: [], copyright: "© 2026 StudioMV" };
  const typography = content?.typography || {};
  const platformTheme = {
    heading_font: typography.heading_font || "Cormorant Garamond",
    body_font: typography.body_font || "Manrope",
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--mv-bg, #fff)", color: "var(--mv-text, #1a1a1a)" }}>
      <header className="border-b" style={{ borderColor: "var(--mv-border, #eee)" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-lg" data-testid="platform-home-link">StudioMV</Link>
          <Link to="/" className="text-sm opacity-70 hover:opacity-100 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-12 lg:py-20" data-testid="platform-page-content">
          {(page.blocks || []).map((b) => (
            <BlockRenderer key={b.id} block={b} theme={platformTheme} forms={[]} services={[]} staff={[]} />
          ))}
        </div>
      </main>

      <footer className="border-t" style={{ borderColor: "var(--mv-border, #eee)" }}>
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs flex flex-wrap items-center justify-between gap-2" style={{ color: "var(--mv-text-muted, #777)" }}>
          <span>{footer.copyright}</span>
          <div className="flex gap-4">
            {(footer.links || []).map((l, i) => (
              <a key={i} href={l.url} className="hover:underline">{l.label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
